"use client";

import { c as _c } from "react/compiler-runtime";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Box, Collapse, IconButton, Stack, useTheme } from "@mui/material";
import React, { useState } from "react";
import { iconButtonHoverSx } from "#/shared/ui/styles/iconButtonHoverStyles";
const HEADER_HEIGHT = 44;
type CollapsiblePanelProps = {
  /** Content rendered in the always-visible header bar */
  header: React.ReactNode;
  children: React.ReactNode;
  /** Whether the panel starts collapsed */
  defaultCollapsed?: boolean;
  /** Controlled collapsed state (overrides internal state) */
  collapsed?: boolean;
  /** Called when the collapse toggle is clicked */
  onToggle?: (collapsed: boolean) => void;
  /** When true, hide the header row. Toggle must be rendered elsewhere (e.g. in parent header). Requires controlled mode (collapsed + onToggle). */
  hideHeaderRow?: boolean;
  /** Content to render when collapsed (e.g. tab bar that expands on click). Replaces the minimal expand button. */
  collapsedContent?: React.ReactNode;
};
export const CollapsiblePanel: React.FC<CollapsiblePanelProps> = t0 => {
  const $ = _c(23);
  const {
    header,
    children,
    defaultCollapsed: t1,
    collapsed: controlledCollapsed,
    onToggle,
    hideHeaderRow: t2,
    collapsedContent
  } = t0;
  const defaultCollapsed = t1 === undefined ? false : t1;
  const hideHeaderRow = t2 === undefined ? false : t2;
  const theme = useTheme();
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const isCollapsed = controlledCollapsed ?? internalCollapsed;
  let t3;
  if ($[0] !== isCollapsed || $[1] !== onToggle) {
    t3 = () => {
      const next = !isCollapsed;
      setInternalCollapsed(next);
      onToggle?.(next);
    };
    $[0] = isCollapsed;
    $[1] = onToggle;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleToggle = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      flexShrink: 0
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== handleToggle || $[5] !== header || $[6] !== hideHeaderRow || $[7] !== isCollapsed || $[8] !== theme) {
    t5 = !hideHeaderRow && <Stack direction="row" sx={{
      alignItems: "center",
      justifyContent: "space-between",
      height: HEADER_HEIGHT,
      px: 1,
      borderBottom: 1,
      borderColor: "divider"
    }}><Box sx={{
        flex: 1,
        minWidth: 0,
        overflow: "hidden"
      }}>{header}</Box><IconButton size="small" onClick={handleToggle} aria-label={isCollapsed ? "Expand panel" : "Collapse panel"} aria-expanded={!isCollapsed} sx={iconButtonHoverSx(theme)}>{isCollapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}</IconButton></Stack>;
    $[4] = handleToggle;
    $[5] = header;
    $[6] = hideHeaderRow;
    $[7] = isCollapsed;
    $[8] = theme;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== collapsedContent || $[11] !== handleToggle || $[12] !== hideHeaderRow || $[13] !== isCollapsed || $[14] !== theme) {
    t6 = hideHeaderRow && isCollapsed && <Box sx={{
      borderTop: 1,
      borderColor: "divider",
      borderRadius: 2
    }}>{collapsedContent ?? <Stack direction="row" sx={{
        alignItems: "center",
        justifyContent: "center",
        height: 36
      }}><IconButton size="small" onClick={handleToggle} aria-label="Expand panel" aria-expanded={false} sx={iconButtonHoverSx(theme)}><ExpandMore fontSize="small" /></IconButton></Stack>}</Box>;
    $[10] = collapsedContent;
    $[11] = handleToggle;
    $[12] = hideHeaderRow;
    $[13] = isCollapsed;
    $[14] = theme;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = !isCollapsed;
  let t8;
  if ($[16] !== children || $[17] !== t7) {
    t8 = <Collapse in={t7} timeout={200} unmountOnExit={false}>{children}</Collapse>;
    $[16] = children;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t5 || $[20] !== t6 || $[21] !== t8) {
    t9 = <Box sx={t4}>{t5}{t6}{t8}</Box>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};
CollapsiblePanel.displayName = "CollapsiblePanel";
export type CollapsiblePanelToggleProps = {
  isCollapsed: boolean;
  onToggle: () => void;
  "aria-label"?: string;
};
export const CollapsiblePanelToggle: React.FC<CollapsiblePanelToggleProps> = t0 => {
  const $ = _c(10);
  const {
    isCollapsed,
    onToggle,
    "aria-label": ariaLabel
  } = t0;
  const theme = useTheme();
  const t1 = ariaLabel ?? (isCollapsed ? "Expand panel" : "Collapse panel");
  const t2 = !isCollapsed;
  let t3;
  if ($[0] !== theme) {
    t3 = iconButtonHoverSx(theme);
    $[0] = theme;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== isCollapsed) {
    t4 = isCollapsed ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />;
    $[2] = isCollapsed;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== onToggle || $[5] !== t1 || $[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = <IconButton size="small" onClick={onToggle} aria-label={t1} aria-expanded={t2} sx={t3}>{t4}</IconButton>;
    $[4] = onToggle;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};